import React from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaArrowLeft, FaShieldAlt, FaKey } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
};

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { email, setEmail, requestForgotOtp } = useAuth();

  /* ── original logic preserved ── */
  const handleSubmit = (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes("@")) {
      alert("Please enter a valid email");
      return;
    }
    requestForgotOtp(value);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      color: T.textPrimary,
      fontFamily: "system-ui,-apple-system,sans-serif",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, position: "relative", overflow: "hidden",
    }}>

      {/* ── ambient glows ── */}
      <div style={{
        position: "absolute", top: "-15%", right: "-10%",
        width: 480, height: 480, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(234,179,8,0.07) 0%,transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-15%", left: "-10%",
        width: 440, height: 440, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(59,130,246,0.07) 0%,transparent 70%)",
        pointerEvents: "none",
      }} />

      {/* ── grid bg ── */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `
          linear-gradient(rgba(234,179,8,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59,130,246,0.02) 1px, transparent 1px)
        `,
        backgroundSize: "44px 44px",
      }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: "relative", zIndex: 10,
          width: "100%", maxWidth: 420,
          background: T.bgCard,
          border: `1px solid rgba(234,179,8,0.22)`,
          borderRadius: 32,
          padding: "clamp(28px,5vw,40px)",
          boxShadow: "0 40px 90px rgba(0,0,0,0.55), 0 0 50px rgba(234,179,8,0.06)",
          overflow: "hidden",
        }}
      >
        {/* top gold accent line */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
        }} />

        {/* corner glow */}
        <div style={{
          position: "absolute", top: -40, right: -40,
          width: 140, height: 140, borderRadius: "50%",
          background: "radial-gradient(ellipse,rgba(234,179,8,0.08) 0%,transparent 70%)",
          pointerEvents: "none",
        }} />

        {/* ── Header ── */}
        <div style={{ textAlign: "center", marginBottom: 36, position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
            style={{
              width: 68, height: 68, margin: "0 auto 20px",
              background: `linear-gradient(135deg,#92400e,${T.accent})`,
              borderRadius: 22,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 12px 32px ${T.accentGlow}`,
            }}
          >
            <FaKey size={24} color="#0a0d14" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            style={{
              fontSize: "clamp(1.5rem,4vw,2rem)",
              fontWeight: 900, textTransform: "uppercase",
              letterSpacing: "-0.03em", color: T.textPrimary, margin: 0,
            }}
          >
            Recover <span style={{ color: T.accent }}>Access</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            style={{ color: T.textSecondary, fontSize: 13, marginTop: 8, fontWeight: 500 }}
          >
            Enter your registered email to receive a recovery OTP.
          </motion.p>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20, position: "relative", zIndex: 1 }}>

          <div>
            <label style={{
              display: "flex", alignItems: "center", gap: 7,
              fontSize: 9, fontWeight: 900, color: T.textMuted,
              textTransform: "uppercase", letterSpacing: "0.18em",
              marginBottom: 10, marginLeft: 2,
            }}>
              <FaEnvelope size={10} /> Email Address
            </label>

            <div style={{ position: "relative" }}>
              <div style={{
                position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
                color: T.textMuted, pointerEvents: "none", fontSize: 14,
              }}>
                <FaEnvelope />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@domain.com"
                required
                style={{
                  width: "100%", boxSizing: "border-box",
                  paddingLeft: 44, paddingRight: 16, paddingTop: 16, paddingBottom: 16,
                  background: "rgba(0,0,0,0.3)",
                  border: `1.5px solid ${T.border}`,
                  borderRadius: 18,
                  fontSize: 14, fontWeight: 600, color: T.textPrimary,
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = T.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${T.accentDim}`;
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <motion.button
            whileHover={{ y: -2, boxShadow: `0 12px 32px ${T.accentGlow}` }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            style={{
              width: "100%", padding: "16px 0",
              background: `linear-gradient(135deg,#92400e,${T.accent})`,
              color: "#0a0d14",
              fontWeight: 900, fontSize: 11,
              textTransform: "uppercase", letterSpacing: "0.18em",
              border: "none", borderRadius: 16, cursor: "pointer",
              boxShadow: `0 8px 24px ${T.accentGlow}`,
              transition: "box-shadow 0.2s, transform 0.2s",
            }}
          >
            Send Reset OTP
          </motion.button>

          <div style={{ textAlign: "center", paddingTop: 4 }}>
            <button
              type="button"
              onClick={() => navigate("/login")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                fontSize: 10, fontWeight: 900, color: T.textMuted,
                textTransform: "uppercase", letterSpacing: "0.18em",
                background: "none", border: "none", cursor: "pointer",
                transition: "color 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.color = T.accent}
              onMouseLeave={e => e.currentTarget.style.color = T.textMuted}
            >
              <FaArrowLeft size={9} /> Return to Security Gate
            </button>
          </div>
        </form>

        {/* ── Security badge ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          style={{
            marginTop: 28, position: "relative", zIndex: 1,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            padding: "10px 18px",
            background: "rgba(0,0,0,0.25)",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
          }}
        >
          <FaShieldAlt size={11} color={T.accent} />
          <span style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em" }}>
            End-to-End Encrypted Recovery
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgetPassword;